import React, { useState } from 'react'
import CountryCodeSelector from '@/components/CountryCodeSelector'

interface LoginFormProps {
  phone: string
  password: string
  countryCode: string
  onPhoneChange: (phone: string) => void
  onPasswordChange: (password: string) => void
  onCountryCodeChange: (countryCode: string) => void
  onSubmit: (e: React.FormEvent) => void
  isLoading: boolean
  error: string
  phoneError: string
  passwordError: string
}

export default function LoginForm({
  phone,
  password,
  countryCode,
  onPhoneChange,
  onPasswordChange,
  onCountryCodeChange,
  onSubmit,
  isLoading,
  error,
  phoneError,
  passwordError
}: LoginFormProps): React.JSX.Element {
  const [showPassword, setShowPassword] = useState(false)

  return (
    <div className="w-full max-w-md mx-auto">
      {/* 标题 */}
      <div className="mb-24">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">登录</h1>
        <p className="text-gray-600"></p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        {/* 国家区号选择 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            国家区号
          </label>
          <CountryCodeSelector
            value={countryCode}
            onChange={(code) => onCountryCodeChange(code)}
            disabled={isLoading}
            className="w-full"
            placeholder="选择国家区号"
          />
        </div>

        {/* 手机号输入 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            手机号
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => onPhoneChange(e.target.value)}
            placeholder="手机号"
            className={`block w-full h-12 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${phoneError ? 'border-red-500' : ''
              }`}
            disabled={isLoading}
          />
          {phoneError && (
            <p className="mt-1 text-sm text-red-600">{phoneError}</p>
          )}
        </div>

        {/* 密码输入 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            密码
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              placeholder="密码"
              className={`mb-10 block w-full h-12 px-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${passwordError ? 'border-red-500' : ''
                }`}
              disabled={isLoading}
            />


            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {showPassword ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.464 8.464M9.878 9.878l-.015-.015M14.12 14.12l4.242 4.242m0 0L20.778 16.95m-1.415 1.415L17.95 20.778" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                )}
              </svg>
            </button>
          </div>
          {passwordError && (
            <p className="mt-1 text-sm text-red-600">{passwordError}</p>
          )}
        </div>

        {/* 记住我和忘记密码 */}
        {/* <div className="flex items-center justify-between">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-blue-600">Remember me</span>
          </label>
          <button
            type="button"
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Forget password
          </button>
        </div> */}

        {/* 通用错误信息 */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}

        {/* 登录按钮 */}
        <button
          type="submit"
          disabled={isLoading || !phone.trim() || !password.trim()}
          className="w-full h-12 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all duration-200 flex items-center justify-center"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              登录中...
            </>
          ) : (
            '登录'
          )}
        </button>

        {/* 服务条款 */}
        {/* <div className="flex items-start space-x-2">
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
          />
          <label className="text-sm text-gray-600">
            I have read and accepted the{' '}
            <button type="button" className="text-blue-600 hover:text-blue-800">
              Terms of Service
            </button>{' '}
            and{' '}
            <button type="button" className="text-blue-600 hover:text-blue-800">
              Privacy Policy
            </button>
          </label>
        </div> */}
      </form>

      {/* 注册链接 - 暂时隐藏 */}
      {/* 
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          还没有账号？{' '}
          <button className="text-blue-600 hover:text-blue-800 font-medium">
            立即注册
          </button>
        </p>
      </div>
      */}
    </div>
  )
}
